/**
 * カレンダー操作をする基底クラス
 */
import * as time from '@/libs/time'

export default class Calendar {
  /**
   * コンストラクタ
   *
   * @param {{period: Object, dates: Object[]}} cal カレンダー情報
   * @param {String} by カレンダーの用途
   */
  constructor (cal, by) {
    // カレンダーの管理期間 begin = cal.period.begin の 00:00
    this._begin = time.join(cal.period.begin, '00:00')

    // カレンダーの管理期間 end = cal.period.end の翌日 00:00
    this._end = time.join(cal.period.end, '00:00')
    this._end.setDate(this._end.getDate() + 1)

    // カレンダー
    this._dates = cal.dates

    // カレンダーの用途
    this._by = by
  }

  /**
   * 予約状況から空き状況を作成する
   *
   * @param {{begin: String, end: String}[]} reserves 予約状況
   * @param {Number} intervalHours インターバル時間
   * @returns {{begin: Date, end: Date}[]} 空き状況を返す
   */
  allows (reserves, intervalHours) {
    const allows = []
    let close

    // 予約可能開始日を得る
    close = this.testClosed(this._begin)
    let first = close ? close.end : time.clone(this._begin)

    // 予約可能終了日を得る
    close = this.testClosed(this._end)
    const last = close ? close.begin : time.clone(this._end)

    for (let n = 0, l = reserves.length; n < l; ++n) {
      // 予約期間にインターバル時間を加味した期間を得る
      const r = this.addInterval(reserves[n], intervalHours)
      if (r.begin > r.end) {
        r.end = last
      }
      r.begin = r.begin || first
      r.end = r.end || last

      // 空き状況を追加する
      if (first < r.begin) {
        allows.push({ begin: first, end: r.begin })
      }

      // 次の空き状況の開始日時を更新する
      first = r.end
    }

    // 最後の空き状況を追加する
    if (first < last) {
      allows.push({ begin: first, end: last })
    }

    return allows
  }

  /**
   * 予約期間にインターバル時間を加味した期間を算出する
   *
   * @param {{begin: String, end: String}} reserve 予約期間
   * @param {Number} intervalHours インターバル時間
   * @returns {{begin: Date, end: Date}} 予約期間にインターバル時間を加味した期間を返す
   */
  addInterval (reserve, intervalHours) {
    return {
      begin: this.securedBegin(time.make(reserve.begin), intervalHours),
      end: this.securedEnd(time.make(reserve.end), intervalHours)
    }
  }

  /**
   * 予約開始日時にインターバル時間を確保する
   *
   * @param {Date} d 元の予約開始日時
   * @param {Number} intervalHours インターバル時間
   * @returns {Date} 予約開始日時を返す
   */
  securedBegin (d, intervalHours) {
    throw new Error('Calendar.securedBegin() is abstract method.')
  }

  /**
   * 予約終了日時にインターバル時間を確保する
   *
   * @param {Date} d 元の予約終了日時
   * @param {Number} intervalHours インターバル時間
   * @returns {Date} 予約終了日時を返す
   */
  securedEnd (d, intervalHours) {
    throw new Error('Calendar.securedEnd() is abstract method.')
  }

  /**
   * 指定日時が営業時間外がどうかをテストする
   *
   * @param {Date} d テストする日時
   * @param {Boolean} outBegin 営業開始時刻を営業時間外とみなす (カレンダー用途 = allows の時のみ)
   * @param {Boolean} outEnd 営業終了時刻を営業時間外とみなす (カレンダー用途 = allows の時のみ)
   * @returns {{begin:Date, end:Date}?} 営業時間外の期間を返す。
   *   営業中なら null を返す。
   */
  testClosed (d, outBegin = false, outEnd = false) {
    const sd = time.format('Y-m-d', d)
    const st = time.format('H:i', d)

    const x = this._dates[sd]
    if (!x) {
      // その日は休日
      return {
        begin: this.previousOpenTime(d),
        end: this.nextOpenTime(d)
      }
    }
    if ((outBegin && this.by === 'allows') ? (st <= x.begin) : (st < x.begin)) {
      // 営業時間前
      return {
        begin: this.previousOpenTime(d),
        end: time.join(sd, x.begin)
      }
    }
    if ((outEnd && this.by === 'allows') ? (x.end <= st) : (x.end < st)) {
      // 営業時間後
      return {
        begin: time.join(sd, x.end),
        end: this.nextOpenTime(d)
      }
    }

    return null
  }

  /**
   * 指定日時以前の営業日時を探す
   *
   * @param {Date} d 指定日時
   * @returns {Date} 指定日時以前の営業日時を返す
   */
  previousOpenTime (d) {
    // 日付のみにする
    const dd = time.clone(d)
    dd.setHours(0, 0, 0, 0)

    // dd を -1 日する
    dd.setDate(dd.getDate() - 1)

    // this._begin まで探す
    while (this._begin <= dd) {
      // this._dates を参照する
      const sd = time.format('Y-m-d', dd)
      const x = this._dates[sd]

      // 営業日が見つかったら 営業日の閉店時間を返す
      if (x) {
        return time.join(sd, x.end)
      }

      // dd を -1 日する
      dd.setDate(dd.getDate() - 1)
    }

    return this._begin
  }

  /**
   * 指定日時以降の営業日時を探す
   *
   * @param {Date} d 指定日時
   * @returns {Date} 指定日時以降の営業日時を返す
   */
  nextOpenTime (d) {
    // 日付のみにする
    const dd = time.clone(d)
    dd.setHours(0, 0, 0, 0)

    // dd を +1 日する
    dd.setDate(dd.getDate() + 1)

    // this._end まで探す
    while (dd < this._end) {
      // this._dates を参照する
      const sd = time.format('Y-m-d', dd)
      const x = this._dates[sd]

      // 営業日が見つかったら 営業日の開店時間を返す
      if (x) {
        return time.join(sd, x.begin)
      }

      // dd を +1 日する
      dd.setDate(dd.getDate() + 1)
    }

    return this._end
  }

  /**
   * 予約カレンダーに表示する情報を作成する
   *
   * @param {{begin: String, end: String}[]} reserves 予約状況
   * @param {Number} intervalHours インターバル時間
   * @returns {any} 予約カレンダーコンポーネント用の情報を返す
   */
  daysInfo (reserves, intervalHours) {
    const allows = this.allows(reserves, intervalHours)

    const days = {}
    for (const date in this._dates) {
      // date の営業状況を取得する
      const worktime = this.workTime(date)

      // 当日に該当する予約可能期間を得る
      const matches = allows.filter(item => {
        return worktime.open < item.end && item.begin <= worktime.close
      })

      if (worktime.closed) {
        // 休業日: 休業日の貸出、返却はユーザには見せる必要はない
        days[date] = {
          status: 'close',
          allows: [{ begin: { here: false }, end: { here: false } }]
        }
      } else if (matches.length === 0) {
        // 予約可能期間がない: 貸出中
        days[date] = {
          status: 'busy',
          allows: []
        }
      } else {
        // 予約可能期間がある
        days[date] = {
          status: 'open',
          allows: this.dayAllows(worktime, matches)
        }
      }

      days[date].hi = this._dates[date].hi
      days[date].holiday = this._dates[date].holiday
      days[date].w = this._dates[date].w
    }
    return days
  }

  /**
   * 指定日時の営業状況を取得する
   *
   * @param {String} date 日付文字列
   * @returns {{closed: Boolean, open: Date, close: Date}} 営業状況を取得する
   */
  workTime (date) {
    const d = this._dates[date]

    const w = {
      closed: false,
      open: null,
      close: null
    }

    // 当日の営業状況を取得する
    if (d) {
      w.closed = false
      w.open = time.join(date, d.begin)
      w.close = time.join(date, d.end)
    } else {
      w.closed = true
      w.open = time.join(date, '00:00')
      w.close = time.join(date, '23:30')
    }

    return w
  }

  /**
   * 予約カレンダーに表示する情報
   *
   * @param {{closed: Boolean, open: Date, close: Date}} worktime 営業状況
   * @param {{begin: Date, end: Date}[]} matches 予約可能期間
   * @returns {{begin: any, end: any, range: {begin: Date, end: Date}}[]} 予約カレンダーに表示する情報を返す
   */
  dayAllows (worktime, matches) {
    return matches.map(range => {
      const begin = {
        here: false,
        em: false,
        time: ''
      }

      const end = {
        here: false,
        em: false,
        time: ''
      }

      if (range.begin < worktime.open) {
        begin.here = false
        begin.em = false
        begin.time = time.format('H:i', worktime.open)
      } else {
        begin.here = true
        begin.em = range.begin.valueOf() !== worktime.open.valueOf()
        begin.time = time.format('H:i', range.begin)
      }

      if (range.end > worktime.close) {
        end.here = false
        end.em = false
        end.time = time.format('H:i', worktime.close)
      } else {
        end.here = true
        end.em = range.end.valueOf() !== worktime.close.valueOf()
        end.time = time.format('H:i', range.end)
      }

      return { begin, end, range }
    })
  }
}
